// src/components/workflow/InformeDocumentos.jsx
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const InformeDocumentos = ({ 
  open, 
  onClose, 
  entidadInfo, 
  documentos, 
  resumenEstados,
  estadoWorkflow 
}) => {
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const componentRef = useRef();

  // Función para imprimir
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Informe_Documentos_${entidadInfo?.nombre || 'Entidad'}_${new Date().getTime()}`,
  });

  // Función para generar PDF
  const handleGenerarPdf = async () => {
    setGenerandoPdf(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Encabezado
      doc.setFontSize(18);
      doc.text('INFORME DE DOCUMENTOS DE INSCRIPCIÓN', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.text(`Entidad: ${entidadInfo?.nombre || 'N/A'}`, 20, 35);
      doc.text(`NIT: ${entidadInfo?.nit || 'N/A'}`, 20, 42);
      doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-CO')}`, 20, 49);
      doc.text(`Estado del proceso: ${getEstadoWorkflowTexto(estadoWorkflow)}`, 20, 56);
      
      // Resumen estadístico
      let yPosition = 70;
      doc.setFontSize(14);
      doc.text('RESUMEN ESTADÍSTICO', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      const resumenData = [
        ['Total de documentos', documentos?.length || 0],
        ['Documentos aprobados', resumenEstados?.documentosAprobados || 0],
        ['Documentos rechazados', resumenEstados?.documentosRechazados || 0],
        ['Documentos en revisión', resumenEstados?.documentosEnRevision || 0],
        ['Documentos pendientes', resumenEstados?.documentosPendientes || 0]
      ];
      
      doc.autoTable({
        startY: yPosition,
        head: [['Concepto', 'Cantidad']],
        body: resumenData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] },
        margin: { left: 20, right: 20 }
      });
      
      // Tabla de documentos detallada
      yPosition = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.text('DETALLE DE DOCUMENTOS', 20, yPosition);
      
      const documentosData = documentos?.map(doc => [
        doc.nombreTipoDocumento || 'N/A',
        getEstadoTexto(doc.estadoDocumentoId),
        doc.fechaSubida ? new Date(doc.fechaSubida).toLocaleDateString('es-CO') : 'N/A',
        doc.usuarioSubida || 'N/A',
        doc.fechaRevision ? new Date(doc.fechaRevision).toLocaleDateString('es-CO') : 'N/A',
        doc.usuarioRevisor || 'N/A',
        doc.comentarioRevision || 'Sin comentarios'
      ]) || [];
      
      doc.autoTable({
        startY: yPosition + 10,
        head: [['Documento', 'Estado', 'F. Subida', 'Usuario Subida', 'F. Revisión', 'Usuario Revisión', 'Observaciones']],
        body: documentosData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 20 },
          2: { cellWidth: 18 },
          3: { cellWidth: 22 },
          4: { cellWidth: 18 },
          5: { cellWidth: 22 },
          6: { cellWidth: 'auto' }
        },
        margin: { left: 10, right: 10 }
      });
      
      // Pie de página
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - 30, doc.internal.pageSize.height - 10);
        doc.text('Generado por Sistema de Gestión Documental', 20, doc.internal.pageSize.height - 10);
      }
      
      // Descargar PDF
      doc.save(`Informe_Documentos_${entidadInfo?.nombre || 'Entidad'}_${new Date().getTime()}.pdf`);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Intente nuevamente.');
    } finally {
      setGenerandoPdf(false);
    }
  };

  const getEstadoChip = (estadoId) => {
    const estados = {
      1: { label: 'Pendiente', color: 'default', icon: <ScheduleIcon /> },
      2: { label: 'Radicado', color: 'primary', icon: <CheckCircleIcon /> },
      3: { label: 'En Revisión', color: 'warning', icon: <WarningIcon /> },
      4: { label: 'Aprobado', color: 'success', icon: <CheckCircleIcon /> },
      5: { label: 'Rechazado', color: 'error', icon: <ErrorIcon /> },
      6: { label: 'Requiere Corrección', color: 'warning', icon: <WarningIcon /> },
      7: { label: 'Pendiente Recarga', color: 'error', icon: <ErrorIcon /> },
      8: { label: 'Subido', color: 'info', icon: <CheckCircleIcon /> }
    };
    
    const estado = estados[estadoId] || { label: 'Desconocido', color: 'default', icon: <ScheduleIcon /> };
    return (
      <Chip 
        label={estado.label} 
        color={estado.color} 
        size="small" 
        icon={estado.icon}
        sx={{ minWidth: 120 }}
      />
    );
  };

  const getEstadoTexto = (estadoId) => {
    const estados = {
      1: 'Pendiente',
      2: 'Radicado',
      3: 'En Revisión',
      4: 'Aprobado',
      5: 'Rechazado',
      6: 'Requiere Corrección',
      7: 'Pendiente Recarga',
      8: 'Subido'
    };
    return estados[estadoId] || 'Desconocido';
  };

  const getEstadoWorkflowTexto = (estado) => {
    const estados = {
      1: 'Iniciado',
      2: 'Registro Incompleto',
      3: 'Registro Completo',
      4: 'En Revisión',
      5: 'Completado/Aprobado',
      6: 'Rechazado/Requiere Atención',
      7: 'Documentos Pendientes',
      8: 'Documentos Cargados'
    };
    return estados[estado] || 'Estado Desconocido';
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          Informe de Documentos de Inscripción
        </Typography>
        <Button onClick={onClose} color="inherit">
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {/* Contenido para imprimir */}
        <div ref={componentRef}>
          {/* Encabezado del informe */}
          <Box sx={{ mb: 4, textAlign: 'center', '@media print': { mb: 2 } }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', '@media print': { fontSize: '18px' } }}>
              INFORME DE DOCUMENTOS DE INSCRIPCIÓN
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ '@media print': { fontSize: '14px' } }}>
              Sistema de Gestión Documental
            </Typography>
          </Box>

          {/* Información de la entidad */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, '@media print': { boxShadow: 'none', border: '1px solid #ccc' } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Información de la Entidad
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Nombre:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{entidadInfo?.nombre || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">NIT:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{entidadInfo?.nit || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Fecha de Generación:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {new Date().toLocaleDateString('es-CO', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Estado del Proceso:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {getEstadoWorkflowTexto(estadoWorkflow)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Resumen estadístico */}
          <Paper elevation={1} sx={{ p: 3, mb: 3, '@media print': { boxShadow: 'none', border: '1px solid #ccc' } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Resumen Estadístico
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {documentos?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Documentos</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                    {resumenEstados?.documentosAprobados || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Aprobados</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.dark' }}>
                    {resumenEstados?.documentosRechazados || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Rechazados</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                    {(resumenEstados?.documentosEnRevision || 0) + (resumenEstados?.documentosPendientes || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Pendientes</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabla de documentos */}
          <Paper elevation={1} sx={{ '@media print': { boxShadow: 'none', border: '1px solid #ccc' } }}>
            <Typography variant="h6" sx={{ p: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Detalle de Documentos
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Documento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Subida</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuario Subida</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Revisión</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuario Revisión</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentos?.map((documento, index) => (
                    <TableRow key={documento.id || index} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {documento.nombreTipoDocumento || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getEstadoChip(documento.estadoDocumentoId)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFecha(documento.fechaSubida)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {documento.usuarioSubida || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFecha(documento.fechaRevision)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {documento.usuarioRevisor || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, wordWrap: 'break-word' }}>
                          {documento.comentarioRevision || 'Sin comentarios'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!documentos || documentos.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay documentos registrados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Pie de página para impresión */}
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'grey.300', display: 'none', '@media print': { display: 'block' } }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              Documento generado automáticamente por el Sistema de Gestión Documental
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              Fecha y hora de generación: {new Date().toLocaleString('es-CO')}
            </Typography>
          </Box>
        </div>

        {/* Mensaje de estado completado */}
        {estadoWorkflow === 5 && (
          <Alert severity="success" sx={{ mt: 2, '@media print': { display: 'none' } }}>
            <Typography variant="body2">
              ✅ El proceso de inscripción ha sido completado exitosamente. 
              Todos los documentos han sido revisados y aprobados.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, '@media print': { display: 'none' } }}>
        <Button 
          onClick={handlePrint} 
          startIcon={<PrintIcon />}
          variant="outlined"
          color="primary"
        >
          Imprimir
        </Button>
        <Button 
          onClick={handleGenerarPdf} 
          startIcon={generandoPdf ? <CircularProgress size={16} /> : <PdfIcon />}
          variant="contained"
          color="primary"
          disabled={generandoPdf}
        >
          {generandoPdf ? 'Generando...' : 'Descargar PDF'}
        </Button>
        <Button onClick={onClose} color="inherit">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InformeDocumentos;